import { siteConfig } from '@afrilink/config';

/**
 * emailSignature — the standard SightHost email signature, as pasteable HTML.
 *
 * Agents were each inventing their own, so the first impression a prospect got
 * of the company varied by whoever happened to be emailing them. This is the
 * one definition.
 *
 * WHY A TABLE AND INLINE STYLES. A signature is pasted into Gmail, Outlook or
 * the Mail app and then lives inside *their* composer. Every constraint the
 * transactional templates have applies here and harder:
 *   - no <style> block, no classes: Gmail strips them on paste;
 *   - no flex or grid: Outlook's Word renderer ignores both (the Studio
 *     quotation email lost its layout to exactly this);
 *   - absolute https image URLs, since there is no page to be relative to;
 *   - no web fonts — the reader's client picks from the stack or falls back.
 *
 * WHY NO BANKING DETAILS. A signature is forwarded, scraped and quoted in
 * every reply chain, which makes it the worst possible carrier for an account
 * number and the easiest thing for someone to alter in a forged reply. The
 * bank-details letter (BankConfirmationLetter) is the controlled artefact for
 * that. What the signature DOES carry is the one line that makes a forged
 * change request fail: our details never change, verify by phone.
 */

export interface EmailSignatureInput {
  fullName: string;
  /** Job title as it should read to a customer — "Sales Consultant", not `sales_agent`. */
  jobTitle: string;
  email: string;
  /** Optional direct/mobile number. Falls back to the switchboard when blank. */
  phone?: string | null;
}

const NAVY = '#1e3a5f';
const ORANGE = '#F6960C';
/** Brand orange darkened to clear 4.5:1 on white — see EmailLayout BRAND.linkOnLight. */
const LINK = '#A86200';
const MUTED = '#6B7280';
const FONT =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

/**
 * The SQUARE app mark, not the wordmark. /images/logo.png is a wide lockup and
 * forcing it into a 52px box distorts it — a signature has room for a glyph,
 * not a lockup.
 */
const LOGO_URL = 'https://portal.sighthost.online/android-icon-192x192.png';

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Digits-only tel: target. SA numbers are stored as +27… or 0…; both work. */
function telHref(raw: string): string {
  return raw.replace(/[^\d+]/g, '');
}

export function buildEmailSignatureHtml(input: EmailSignatureInput): string {
  const name = esc(input.fullName.trim() || 'Your Name');
  const title = esc(input.jobTitle.trim() || 'Sales Consultant');
  const email = esc(input.email.trim());
  const phone = (input.phone ?? '').trim();
  const site = siteConfig.url.replace(/^https?:\/\//, '');

  const contactRow = (label: string, value: string) =>
    `<tr><td style="padding:1px 0;font:400 12px/1.5 ${FONT};color:${MUTED};">` +
    `<span style="color:${MUTED};">${label}</span>&nbsp;${value}</td></tr>`;

  const phoneLine = phone
    ? contactRow(
        'D',
        `<a href="tel:${esc(telHref(phone))}" style="color:${LINK};text-decoration:none;">${esc(phone)}</a>`,
      )
    : '';

  // The switchboard is always shown: a customer who cannot reach the agent must
  // still be able to reach the company, which is also the number they are told
  // to verify banking changes on.
  const switchboard = contactRow(
    'T',
    `<a href="tel:${esc(telHref(siteConfig.phone))}" style="color:${LINK};text-decoration:none;">${esc(siteConfig.phoneDisplay)}</a>`,
  );

  return `<table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;font:400 13px/1.5 ${FONT};color:${NAVY};">
  <tr>
    <td style="padding:0 16px 0 0;vertical-align:top;">
      <img src="${LOGO_URL}" alt="${esc(siteConfig.name)}" width="52" height="52" style="display:block;width:52px;height:52px;border:0;outline:none;text-decoration:none;" />
    </td>
    <td style="padding:0;vertical-align:top;border-left:3px solid ${ORANGE};padding-left:16px;">
      <table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
        <tr>
          <td style="padding:0 0 1px 0;font:700 15px/1.3 ${FONT};color:${NAVY};">${name}</td>
        </tr>
        <tr>
          <td style="padding:0 0 6px 0;font:400 12px/1.4 ${FONT};color:${MUTED};">${title} &middot; ${esc(siteConfig.legalName)}</td>
        </tr>
        ${phoneLine}
        ${switchboard}
        ${contactRow('E', `<a href="mailto:${email}" style="color:${LINK};text-decoration:none;">${email}</a>`)}
        ${contactRow('W', `<a href="${esc(siteConfig.url)}" style="color:${LINK};text-decoration:none;">${esc(site)}</a>`)}
        <tr>
          <td style="padding:8px 0 0 0;">
            <div style="border-top:1px solid #E5E7EB;padding-top:6px;font:400 10px/1.45 ${FONT};color:${MUTED};">
              ${esc(siteConfig.legalName)} &middot; Reg. ${esc(siteConfig.registration)}<br />
              Our banking details never change. If you are told they have, do not pay &mdash; phone us on ${esc(siteConfig.phoneDisplay)} to confirm.
            </div>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>`;
}

/** Plain-text fallback, for clients that strip HTML and for the clipboard's text/plain flavour. */
export function buildEmailSignatureText(input: EmailSignatureInput): string {
  const lines = [
    input.fullName.trim() || 'Your Name',
    `${input.jobTitle.trim() || 'Sales Consultant'} | ${siteConfig.legalName}`,
  ];
  if (input.phone?.trim()) lines.push(`D ${input.phone.trim()}`);
  lines.push(`T ${siteConfig.phoneDisplay}`);
  lines.push(`E ${input.email.trim()}`);
  lines.push(`W ${siteConfig.url}`);
  lines.push('');
  lines.push(`${siteConfig.legalName} | Reg. ${siteConfig.registration}`);
  lines.push(
    `Our banking details never change. If you are told they have, do not pay - phone us on ${siteConfig.phoneDisplay} to confirm.`,
  );
  return lines.join('\n');
}

/**
 * A standalone .html file wrapping the signature, for the download.
 *
 * Carries its own instructions because the failure mode is predictable: an
 * agent downloads the file, opens it, and copies from the *rendered* page —
 * which is right — or opens it in a text editor and pastes the source, which
 * produces visible markup in their mail. The page says which to do.
 */
export function buildEmailSignatureDocument(input: EmailSignatureInput): string {
  const sig = buildEmailSignatureHtml(input);
  const name = esc(input.fullName.trim() || 'Your Name');
  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${name} — SightHost email signature</title>
</head>
<body style="margin:0;padding:32px;background:#F9FAFB;font:400 14px/1.6 ${FONT};color:${NAVY};">
  <div style="max-width:680px;margin:0 auto;">
    <h1 style="margin:0 0 4px;font-size:18px;color:${NAVY};">Your SightHost email signature</h1>
    <p style="margin:0 0 20px;color:${MUTED};font-size:13px;">
      Select everything inside the box below, copy it, and paste it into your
      mail client's signature setting (Gmail: Settings &rarr; General &rarr;
      Signature. Outlook: File &rarr; Options &rarr; Mail &rarr; Signatures).
      Copy from this page as it looks &mdash; do not open this file in a text
      editor and copy the code, or the markup will show in your emails.
    </p>
    <div style="background:#ffffff;border:1px solid #E5E7EB;border-radius:8px;padding:24px;">
      ${sig}
    </div>
    <p style="margin:20px 0 0;color:${MUTED};font-size:12px;">
      Generated by the SightHost portal. If your name, title or number changes,
      generate a fresh one rather than editing this file.
    </p>
  </div>
</body>
</html>`;
}
